import { apiClient, getApiUrl } from '../lib/apiClient';
import type { User, CreateUserRequest, UpdateUserRequest, Pagination } from '../types/api';
import { 
  extractListData, 
  extractSingleData,
  type RailsDirectListResponse,
  type RailsSingleDirectResponse
} from '../utils/railsResponseHandler';
import { withQueryErrorHandling } from '../utils/apiErrorHandler';

// Types now imported from types/api.ts to match OpenAPI spec
export interface UsersParams {
  page?: number; // OpenAPI parameter
  limit?: number; // OpenAPI parameter  
  role?: string;
  search?: string;
  active?: boolean;
  school_id?: string;
}

// Get users with OpenAPI-compliant response structure
export const getUsers = async (params?: UsersParams): Promise<{ users: User[]; pagination: Pagination }> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<User>>(getApiUrl('/users'), { params });
    
    // Extract users using the Rails response handler to handle JSON:API format
    const users = extractListData<User>(response.data);
    
    return {
      users: users,
      pagination: response.data.pagination || {
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_count: users.length,
        has_previous: false,
        has_next: false
      }
    };
  });
};

// Get user by ID
export const getUser = async (id: string): Promise<User> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsSingleDirectResponse<User>>(getApiUrl(`/users/${id}`));
    return extractSingleData<User>(response.data);
  });
};

// Create user
export const createUser = async (data: CreateUserRequest): Promise<User> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleDirectResponse<User>>(getApiUrl('/users'), data);
    return extractSingleData<User>(response.data);
  });
};

// Update user  
export const updateUser = async (id: string, data: UpdateUserRequest): Promise<User> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.put<RailsSingleDirectResponse<User>>(getApiUrl(`/users/${id}`), data); // OpenAPI specifies PUT
    return extractSingleData<User>(response.data);
  });
};

// Delete user
export const deleteUser = async (id: string): Promise<void> => {
  return withQueryErrorHandling(async () => {
    await apiClient.delete(getApiUrl(`/users/${id}`)); // OpenAPI returns 204 No Content
  });
};

// Reset user password - DEPRECATED: Not in OpenAPI spec
// TODO: Remove this function or implement in Rails API according to OpenAPI spec
export const resetUserPassword = async (id: string, data: {
  new_password?: string;
  send_email?: boolean;
}): Promise<{ message: string }> => {
  return withQueryErrorHandling(async () => {
    console.warn('resetUserPassword is not in OpenAPI spec and may fail');
    const response = await apiClient.post(getApiUrl(`/users/${id}/reset_password`), data);
    return response.data;
  });
};

// Toggle user status - DEPRECATED: Not in OpenAPI spec
// TODO: Use updateUser with { active } instead
export const toggleUserStatus = async (id: string, active: boolean): Promise<User> => {
  return withQueryErrorHandling(async () => {
    console.warn('toggleUserStatus is not in OpenAPI spec. Use updateUser instead.');
    // Use the standard update endpoint instead
    return updateUser(id, { active } as UpdateUserRequest);
  });
};

// Send welcome email - DEPRECATED: Not in OpenAPI spec
// TODO: Remove this function or implement in Rails API according to OpenAPI spec
export const sendWelcomeEmail = async (id: string): Promise<{ message: string }> => {
  return withQueryErrorHandling(async () => {
    console.warn('sendWelcomeEmail is not in OpenAPI spec and may fail');
    const response = await apiClient.post(getApiUrl(`/users/${id}/welcome_email`));
    return response.data;
  });
};